import { formatCents } from "@caloteiros/shared";
import { useEffect } from "react";
import type { ReactElement, ReactNode } from "react";
import { initials } from "../lib/format";
import { IconX } from "./Icons";

export function Avatar({
  name,
  color,
  size = "md",
  title,
}: {
  name: string;
  color: string;
  size?: "sm" | "md" | "lg";
  title?: string;
}): ReactElement {
  const className = size === "md" ? "avatar" : `avatar avatar-${size}`;
  return (
    <span className={className} style={{ backgroundColor: color }} title={title ?? name}>
      {initials(name)}
    </span>
  );
}

/** Valor formatado. `signed` pinta de verde/magenta e escreve o sinal. */
export function Money({
  cents,
  signed = false,
  className = "",
}: {
  cents: number;
  signed?: boolean;
  className?: string;
}): ReactElement {
  if (!signed) {
    return <span className={`numeric ${className}`}>{formatCents(cents)}</span>;
  }
  const tone = cents > 0 ? "positive" : cents < 0 ? "negative" : "";
  return (
    <span className={`numeric ${tone} ${className}`}>
      {cents > 0 ? "+" : cents < 0 ? "−" : ""}
      {formatCents(Math.abs(cents))}
    </span>
  );
}

export function EmptyState({
  emoji,
  title,
  description,
  action,
}: {
  emoji: string;
  title: string;
  description?: string;
  action?: ReactNode;
}): ReactElement {
  return (
    <div className="empty">
      <span className="empty-emoji" aria-hidden="true">
        {emoji}
      </span>
      <h3>{title}</h3>
      {description ? <p className="muted">{description}</p> : null}
      {action}
    </div>
  );
}

export function Skeleton({ height = 64, count = 3 }: { height?: number; count?: number }) {
  return (
    <div className="stack" aria-hidden="true">
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="skeleton" style={{ height }} />
      ))}
    </div>
  );
}

export function Spinner(): ReactElement {
  return <span className="spinner" role="status" aria-label="Carregando" />;
}

export function LoadingScreen(): ReactElement {
  return (
    <div className="empty" style={{ minHeight: "60svh" }}>
      <Spinner />
      <p className="muted">Carregando...</p>
    </div>
  );
}

/**
 * A folha que sobe de baixo no celular e vira modal no desktop. Fecha no Esc,
 * no clique fora e trava a rolagem do fundo enquanto esta aberta.
 */
export function Sheet({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}): ReactElement | null {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="sheet-backdrop"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="sheet" role="dialog" aria-modal="true" aria-label={title}>
        <div className="sheet-handle" aria-hidden="true" />
        <div className="row-between" style={{ marginBottom: "var(--space-4)" }}>
          <h2>{title}</h2>
          <button type="button" className="btn-icon" onClick={onClose} aria-label="Fechar">
            <IconX />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirmar",
  destructive = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}): ReactElement | null {
  return (
    <Sheet open={open} onClose={onCancel} title={title}>
      <div className="stack">
        <p className="muted">{message}</p>
        <div className="row" style={{ marginTop: "var(--space-2)" }}>
          <button type="button" className="btn btn-ghost grow" onClick={onCancel}>
            Cancelar
          </button>
          <button
            type="button"
            className={`btn grow ${destructive ? "btn-danger" : "btn-primary"}`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </Sheet>
  );
}

export function Field({
  label,
  error,
  hint,
  as = "label",
  children,
}: {
  label: string;
  error?: string | undefined;
  hint?: string;
  /**
   * "label" envolve o campo, o que da o nome acessivel de graca. Use "div"
   * quando o bloco tiver mais de um controle dentro — um <label> so pode
   * apontar para um.
   */
  as?: "label" | "div";
  children: ReactNode;
}): ReactElement {
  const Wrapper = as;
  return (
    <Wrapper className="field">
      <span className="label">{label}</span>
      {children}
      {hint && !error ? <span className="hint">{hint}</span> : null}
      {error ? <span className="error-text">{error}</span> : null}
    </Wrapper>
  );
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
  label: string;
}): ReactElement {
  return (
    <div className="segmented" role="tablist" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="tab"
          aria-selected={value === option.value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
